"use client";

import { useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Copy, Check, Paperclip, X } from "lucide-react";
import { toast } from "sonner";

export interface BugReportContext {
  screen?: string;
  fileType?: string;
}

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called when opening the modal or submitting so logs are captured at action time, not render time. */
  getLogs: () => string;
  context?: BugReportContext;
}

const MAX_ATTACHMENT_SIZE_MB = 5;
const MAX_ATTACHMENT_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;
const MAX_ATTACHMENT_COUNT = 2;
const ACCEPT_ATTACHMENT = "image/jpeg,image/png,image/webp,image/gif,application/pdf";

export default function BugReportModal({
  isOpen,
  onClose,
  getLogs,
  context = {},
}: BugReportModalProps) {
  const [description, setDescription] = useState("");
  const [includeLogs, setIncludeLogs] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportId, setReportId] = useState<string | null>(null);
  const [idCopied, setIdCopied] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

  const resetState = useCallback(() => {
    setDescription("");
    setIncludeLogs(true);
    setIsSubmitting(false);
    setError(null);
    setReportId(null);
    setIdCopied(false);
    setAttachedFiles([]);
  }, []);

  const addFiles = useCallback((newFiles: FileList | null) => {
    if (!newFiles?.length) return;
    setAttachedFiles((prev) => {
      const next = [...prev];
      for (let i = 0; i < newFiles.length && next.length < MAX_ATTACHMENT_COUNT; i++) {
        next.push(newFiles[i]);
      }
      return next.slice(0, MAX_ATTACHMENT_COUNT);
    });
  }, []);

  const removeFile = useCallback((index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    resetState();
  }, [onClose, resetState]);

  const handleSubmit = useCallback(async () => {
    setError(null);
    const oversize = attachedFiles.find((f) => f.size > MAX_ATTACHMENT_BYTES);
    if (oversize) {
      setError(`Plik „${oversize.name}” jest za duży. Maks. ${MAX_ATTACHMENT_SIZE_MB} MB na plik.`);
      return;
    }
    setIsSubmitting(true);
    try {
      const logsToSend = includeLogs ? getLogs() : "";
      const payload = {
        description: description.trim() || "(brak opisu)",
        logs: logsToSend,
        context,
      };
      const formData = new FormData();
      formData.append("payload", JSON.stringify(payload));
      attachedFiles.forEach((file) => formData.append("file", file));
      const res = await fetch("/api/bug-report", {
        method: "POST",
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error ?? "Nie udało się wysłać zgłoszenia. Spróbuj ponownie.");
        return;
      }
      setReportId(data.id ?? null);
      if (data.id) {
        toast.success("Zgłoszenie wysłane");
      }
    } catch {
      setError("Błąd połączenia. Sprawdź internet i spróbuj ponownie.");
    } finally {
      setIsSubmitting(false);
    }
  }, [description, includeLogs, getLogs, context, attachedFiles]);

  const copyIdToClipboard = useCallback(async () => {
    if (!reportId) return;
    try {
      await navigator.clipboard.writeText(reportId);
      setIdCopied(true);
      toast.success("ID skopiowane do schowka");
      setTimeout(() => setIdCopied(false), 2000);
    } catch {
      toast.error("Nie udało się skopiować ID");
    }
  }, [reportId]);

  const copyLogsToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(getLogs());
      toast.success("Logi skopiowane do schowka");
    } catch {
      toast.error("Nie udało się skopiować logów");
    }
  }, [getLogs]);

  const showForm = reportId === null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Zgłoś problem</DialogTitle>
          <DialogDescription>
            {showForm
              ? "Opisz krótko, co poszło nie tak. Do zgłoszenia zostaną dołączone logi diagnostyczne (możesz je wyłączyć)."
              : "Zgłoszenie zostało zapisane. Zachowaj poniższy ID — możesz go przekazać developerowi razem z dodatkowymi informacjami lub zrzutami ekranu."}
          </DialogDescription>
        </DialogHeader>

        {showForm ? (
          <>
            <div className="space-y-2">
              <Label htmlFor="bug-description">Opis (opcjonalnie)</Label>
              <textarea
                id="bug-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="np. Aplikacja zawiesiła się po wybraniu zdjęcia..."
                className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                maxLength={2000}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="include-logs"
                checked={includeLogs}
                onCheckedChange={(checked) => setIncludeLogs(checked === true)}
              />
              <Label htmlFor="include-logs" className="text-sm font-normal cursor-pointer">
                Dołącz logi diagnostyczne
              </Label>
            </div>
            <div className="space-y-2">
              <Label>Dołącz pliki (opcjonalnie, max {MAX_ATTACHMENT_COUNT})</Label>
              <p className="text-xs text-muted-foreground">
                Zdjęcia lub PDF, max {MAX_ATTACHMENT_SIZE_MB} MB na plik — np. pliki, przy których wystąpił błąd.
              </p>
              {attachedFiles.length > 0 && (
                <ul className="space-y-2">
                  {attachedFiles.map((file, index) => (
                    <li key={index} className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2 text-sm">
                      <span className="truncate flex-1" title={file.name}>
                        {file.name}
                      </span>
                      <span className="text-muted-foreground shrink-0">
                        ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0 h-8 w-8"
                        onClick={() => removeFile(index)}
                        aria-label="Usuń załącznik"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
              {attachedFiles.length < MAX_ATTACHMENT_COUNT && (
                <label className="flex items-center gap-2 rounded-md border border-dashed border-input px-3 py-2 text-sm cursor-pointer hover:bg-muted/50">
                  <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="text-muted-foreground">
                    {attachedFiles.length === 0 ? "Wybierz plik…" : "Dodaj drugi plik…"}
                  </span>
                  <input
                    type="file"
                    accept={ACCEPT_ATTACHMENT}
                    className="sr-only"
                    multiple
                    onChange={(e) => {
                      addFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                </label>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Możesz też{" "}
              <button
                type="button"
                onClick={copyLogsToClipboard}
                className="underline hover:no-underline"
              >
                skopiować logi do schowka
              </button>{" "}
              i wysłać je ręcznie.
            </p>
            {error && (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Anuluj
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? "Wysyłanie…" : "Wyślij zgłoszenie"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2">
              <code className="text-sm font-mono flex-1" aria-label="ID zgłoszenia">
                {reportId}
              </code>
              <Button
                variant="ghost"
                size="icon"
                onClick={copyIdToClipboard}
                aria-label="Skopiuj ID"
              >
                {idCopied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={handleClose}>Zamknij</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
